// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Loader, Grid, Message, Button, Popup, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";

import _ from "lodash";
import moment from "moment";

import { useUserAuth } from "../../../../hooks/useUserAuth";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import * as weatherDataActions from "../../../../redux/actions/weatherDataActions";
import * as excelGenerationFunctions from "../../../../utilities/excelGeneration";

import styles from "../../../Lumber/Timecourse";

import PropTypes from "prop-types";

import { ApplicationArea } from "../../../Lumber/ApplicationAreas";

const TrialWeatherGraphs = ({ trialName, setGrowingSeason }) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const dispatch = useDispatch();
	const moduleNavigation = useModuleNavigation();

	//const clientId = useSelector((state) => (state.clients ? state.clients.currentId : null));
	//-- Data Sources
	const [historicalWeatherData, setHistoricalWeatherData] = useState([]);
	const [dailyTempChartOptions, setDailyTempChartOptions] = useState({ data: [], layout: {} });
	const [dailyRainfallChartOptions, setDailyRainfallChartOptions] = useState({ data: [], layout: {} });
	const [dailyGDUChartOptions, setDailyGDUChartOptions] = useState({ data: [], layout: {} });
	const [cumulativeRainfallChartOptions, setCumulativeRainfallChartOptions] = useState({ data: [], layout: {} });
	const [currentGDUByDay, setCurrentGDUByDay] = useState(null);
	const [gduByDaySincePlanting, setGDUByDaySincePlanting] = useState(null);
	const [weatherDataSincePlanting, setWeatherDataSincePlanting] = useState(null);
	const [gduByDayHistorical, setGDUByDayHistorical] = useState(null);
	const [trialDailyWeather, setTrialDailyWeather] = useState(null);
	//Used to reduce toast message warnings for when the plant date is greater than the harvest date
	const [isPlantDateValid, setIsPlantDateValid] = useState(true);
	const [isCropValid, setIsCropValid] = useState(true);

	//-- UI Control
	const [loading, setLoading] = useState(true);
	const [dailyChartLoading, setDailyChartLoading] = useState(true);
	const [gduChartLoading, setGDUChartLoading] = useState(true);

	//-- Excel
	const [excelDownloadLoading, setExcelDownloadLoading] = useState(false);

	//Used for historical weather data toast message when there is no data
	const [isCreateDone, setIsCreateDone] = useState(false);

	//Create Historical Data
	useEffect(() => {
		if (userAuth.isReady) {
			createHistoricalWeatherData();
		}
	}, [userAuth.isReady]);

	useEffect(() => {
		if (
			moduleNavigation.trialId &&
			(!historicalWeatherData || historicalWeatherData?.length === 0) &&
			userAuth.isReady &&
			isCreateDone
		) {
			getHistoricalWeatherData(moduleNavigation.trialId);
		}
	}, [moduleNavigation.trialId, userAuth.isReady, historicalWeatherData, isCreateDone]);

	useEffect(() => {
		if (
			moduleNavigation.trialId &&
			(!currentGDUByDay || currentGDUByDay?.length === 0) &&
			userAuth.isReady &&
			isPlantDateValid &&
			isCropValid
		) {
			getCurrentGDUByDay(moduleNavigation.trialId);
		}
	}, [moduleNavigation.trialId, userAuth.isReady, currentGDUByDay, isPlantDateValid, isCropValid]);

	useEffect(() => {
		if (currentGDUByDay && (currentGDUByDay?.length > 0 || !isCropValid) && dailyChartLoading) {
			getTrialDailyWeather(moduleNavigation.trialId);
		}
	}, [currentGDUByDay]);

	useEffect(() => {
		if (historicalWeatherData?.length > 0 && trialDailyWeather?.length > 0) {
			setDailyChartLoading(true);
			createChartForDays();
			createChartForRainfall();
			createChartForGDUDaysRainfall();
			setDailyChartLoading(false);
		}
	}, [historicalWeatherData, trialDailyWeather]);

	useEffect(() => {
		if (currentGDUByDay?.length > 0 && gduByDayHistorical?.length > 0) {
			setGDUChartLoading(true);
			createChartForGDUDays();
			setGDUChartLoading(false);
		}
	}, [currentGDUByDay, gduByDayHistorical, gduByDaySincePlanting, historicalWeatherData, trialDailyWeather]);

	async function createHistoricalWeatherData() {
		const accessToken = await getTokenSilently();
		setLoading(true);
		dispatch(
			weatherDataActions.createHistoricalWeatherData(moduleNavigation.trialId, userAuth.currentClientId, accessToken),
		)
			.then((res) => {
				if (res.statusCode === 200) {
					setIsCreateDone(true);
				}
			})
			.catch((error) => {
				toast.error("An Error has occured while creating historical weather data");
				console.log(error);
			});
	}

	async function getHistoricalWeatherData(trialId) {
		const accessToken = await getTokenSilently();

		dispatch(weatherDataActions.getHistoricalWeatherData(trialId, userAuth.currentClientId, accessToken))
			.then((res) => {
				if (res.data.length !== 0) {
					setHistoricalWeatherData(res.data.item1);
					setGrowingSeason(res.data.item2);
					getGDUByDayHistorical(moduleNavigation.trialId);
				} else {
					toast.warn("Could not find historical weather data");
				}
			})
			.catch((err) => {
				console.log(err);
			});
	}

	async function getCurrentGDUByDay(trialId) {
		const accessToken = await getTokenSilently();

		await dispatch(weatherDataActions.getGDUForTrial(trialId, userAuth.currentClientId, false, accessToken, true))
			.then((res) => {
				setCurrentGDUByDay(res.data.item1);
				setGDUByDaySincePlanting(res.data.item2);
				setIsPlantDateValid(res.data.item3);
				setIsCropValid(res.data.item5);
				setWeatherDataSincePlanting(res.data.item6);
				if (!res.data.item3 && !currentGDUByDay) {
					toast.error("Plant date year can not be greater than the harvest date year");
					setLoading(false);
					setDailyChartLoading(false);
					setGDUChartLoading(false);
				}
				if (!res.data.item4) {
					toast.warn("No plant date is set");
				}
				if (res.data.item3 && res.data.item2.length === 0 && !currentGDUByDay && res.data.item4 && res.data.item5) {
					toast.warn("Could not find since planting weather data");
				}
			})
			.catch((err) => {
				console.log(err);
				setLoading(false);
				setDailyChartLoading(false);
				setGDUChartLoading(false);
			});
	}

	async function getGDUByDayHistorical(trialId) {
		const accessToken = await getTokenSilently();

		dispatch(weatherDataActions.getGDUForTrialHistorical(trialId, userAuth.currentClientId, accessToken))
			.then((res) => {
				setGDUByDayHistorical(res.data.item1);
				setIsCropValid(res.data.item2);
			})
			.catch((err) => {
				console.log(err);
			});
	}

	async function getTrialDailyWeather(trialId) {
		const accessToken = await getTokenSilently();

		dispatch(weatherDataActions.getTrialDailyWeather(trialId, userAuth.currentClientId, accessToken))
			.then((res) => {
				setTrialDailyWeather(res.data);
				setLoading(false);
				if (res.data === 0) {
					toast.warn("Could not find current daily weather data");
				}
			})
			.catch((err) => {
				console.log(err);
			});
	}

	async function generateWeatherDataExcel() {
		const accessToken = await getTokenSilently();
		setExcelDownloadLoading(true);
		let currentDateTimeString = JSON.stringify(new Date().toLocaleString());
		dispatch(
			weatherDataActions.generateWeatherDataExcel(
				moduleNavigation.trialId,
				currentDateTimeString,
				userAuth.currentClientId,
				accessToken,
			),
		)
			.then((res) => {
				let excelFileName = `${trialName}_TrialWeatherData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(res.data, excelFileName, false);

				setExcelDownloadLoading(false);
			})
			.catch((err) => {
				setExcelDownloadLoading(false);
				console.log(err);
				toast.error("Error downloading excel file. Please try again.");
			});
	}

	//Function to set the layout of any chart
	function configureChartLayout(title, yaxis) {
		return {
			title: { text: title },
			yaxis: { title: { text: yaxis } },
			xaxis: { tickangle: 0, nticks: 8, automargin: true },
			legend: { orientation: "h", x: 0.5, xanchor: "center", y: -0.15, yanchor: "top" },
			margin: { b: 100, t: 40 },
			hovermode: "x unified",
			hoverlabel: { bgcolor: "white", bordercolor: "#ccc" },
			autosize: true,
		};
	}

	//Daily Temperature Chart
	function createChartForDays() {
		let xLabels = [];
		let histAvgY = [],
			histHighY = [],
			histLowY = [];
		let avgY = [],
			highY = [],
			lowY = [];

		_.map(historicalWeatherData, (weatherData) => {
			//Set Historical weather
			xLabels.push(weatherData.day);
			histAvgY.push(weatherData.temperature);
			histHighY.push(weatherData.dailyHigh);
			histLowY.push(weatherData.dailyLow);

			//Set Daily Current Weather
			let tdw = trialDailyWeather?.find((el) => el.day === weatherData.day);

			//If trial weather data exists for the same day as the historical data
			if (tdw) {
				avgY.push(tdw.temperature);
				highY.push(tdw.dailyHigh);
				lowY.push(tdw.dailyLow);
			}
			//Otherwise set the daily temperature value to empty
			else {
				avgY.push(null);
				highY.push(null);
				lowY.push(null);
			}
		});

		//Set chart options for each data set
		const data = [
			{
				name: "30-yr Daily Avg",
				type: "scatter",
				mode: "lines",
				line: { color: "green", dash: "dot", shape: "spline" },
				x: xLabels,
				y: histAvgY,
			},
			{
				name: "30-yr Daily High",
				type: "scatter",
				mode: "lines",
				line: { color: "red", dash: "dot", shape: "spline" },
				x: xLabels,
				y: histHighY,
			},
			{
				name: "30-yr Daily Low",
				type: "scatter",
				mode: "lines",
				line: { color: "blue", dash: "dot", shape: "spline" },
				x: xLabels,
				y: histLowY,
			},
			{
				name: "Daily Avg",
				type: "scatter",
				mode: "lines",
				line: { color: "lightgreen" },
				x: xLabels,
				y: avgY,
			},
			{
				name: "Daily High",
				type: "scatter",
				mode: "lines",
				line: { color: "#FF7F7F" },
				x: xLabels,
				y: highY,
			},
			{
				name: "Daily Low",
				type: "scatter",
				mode: "lines",
				line: { color: "lightblue" },
				x: xLabels,
				y: lowY,
			},
		];

		setDailyTempChartOptions({ data, layout: configureChartLayout("Temperature", "Daily Temps °F") });
	}

	function createChartForRainfall() {
		let xLabels = [];
		let rainfallY = [];

		_.map(trialDailyWeather, (weatherData) => {
			//Set historical daily rainfall data
			xLabels.push(weatherData.day);
			rainfallY.push(weatherData.precipitation);
		});

		//Set chart options for daily historical rainfall data
		const data = [
			{
				name: "Daily Rainfall",
				type: "scatter",
				mode: "lines",
				x: xLabels,
				y: rainfallY,
			},
		];

		setDailyRainfallChartOptions({ data, layout: configureChartLayout("Rainfall", "Daily Rainfall (inches)") });
	}

	function createChartForGDUDays() {
		let histGDUX = [],
			histGDUY = [];
		let currentGDUX = [],
			currentGDUY = [];
		let sincePlantingX = [],
			sincePlantingY = [];
		let sincePlantingHistX = [],
			sincePlantingHistY = [];
		let previousHistoricalGDU = 0.0;
		let previousGDU = 0.0;
		let previousGDUSincePlanting = 0.0;
		let previousGDUSincePlantingHistorical = 0.0;
		let lastDaySincePlanting = gduByDaySincePlanting?.find((el, index) => index === gduByDaySincePlanting.length - 1);
		let firstDaySincePlanting = gduByDaySincePlanting?.find((el, index) => index === 0);
		let lastDayReached = false;

		_.map(gduByDayHistorical, (gduHistorical, index) => {
			//Set historical gdu weather data
			histGDUX.push(gduHistorical.monthDay);
			histGDUY.push(gduHistorical.gdu + previousHistoricalGDU);

			//Set current year daily gdu data
			let dailyGDU = currentGDUByDay?.find((el) => el.monthDay === gduHistorical.monthDay);

			//If daily gdu data exists for the same day as the historical data
			if (dailyGDU) {
				currentGDUX.push(dailyGDU.monthDay);
				currentGDUY.push(dailyGDU.gdu + previousGDU);
				previousGDU = dailyGDU.gdu + previousGDU;
			}
			//Otherwise set the daily gdu value to empty
			else {
				currentGDUX.push(gduHistorical.monthDay);
				currentGDUY.push(null);
			}

			//Set current year gdu data since planting
			let gduSincePlanting =
				gduHistorical.monthDay === firstDaySincePlanting?.monthDay
					? null
					: gduByDaySincePlanting?.find((el) => el.monthDay === gduHistorical.monthDay);

			//This is used to handle current year situations where we don't have since planting data for the entire year
			if (!lastDayReached) {
				lastDayReached = gduHistorical.monthDay === lastDaySincePlanting?.monthDay ? true : false;
			}

			//If current year gdu data since planting exists for the same day as the historical data
			if (gduSincePlanting) {
				//Use previous day's since planting gdus to account of planting date (should be null on plant date)
				let previousGDUFromIndex = gduByDayHistorical?.find(
					(el, indexHist) => indexHist === (index === 0 ? 0 : index - 1),
				);
				let previousGDUSincePlantingFromIndex = gduByDaySincePlanting?.find(
					(el) => el.monthDay == previousGDUFromIndex.monthDay,
				);
				let previousGDUSincePlantingHistoricalFromIndex = gduByDayHistorical?.find(
					(el) => el.monthDay == previousGDUFromIndex.monthDay,
				);

				//-- Check if all data is found
				if (previousGDUFromIndex && previousGDUSincePlantingFromIndex && previousGDUSincePlantingHistoricalFromIndex) {
					//Current year gdu data since planting
					sincePlantingX.push(gduSincePlanting.monthDay);
					sincePlantingY.push(previousGDUSincePlantingFromIndex.gdu + previousGDUSincePlanting);
					//Historical gdu data since planting
					sincePlantingHistX.push(gduHistorical.monthDay);
					sincePlantingHistY.push(previousGDUSincePlantingHistoricalFromIndex.gdu + previousGDUSincePlantingHistorical);
					previousGDUSincePlanting = previousGDUSincePlantingFromIndex.gdu + previousGDUSincePlanting;
					previousGDUSincePlantingHistorical =
						previousGDUSincePlantingHistoricalFromIndex.gdu + previousGDUSincePlantingHistorical;
				}
			}
			//Otherwise set the current gdu value to empty
			else {
				sincePlantingX.push(gduHistorical.monthDay);
				sincePlantingY.push(null);
				//Normal operation if we don't have since planting data
				if (!lastDayReached) {
					sincePlantingHistX.push(gduHistorical.monthDay);
					sincePlantingHistY.push(null);
				}
				//Set since planting GDU data from historical weather data
				else {
					sincePlantingHistX.push(gduHistorical.monthDay);
					sincePlantingHistY.push(gduHistorical.gdu + previousGDUSincePlantingHistorical);
					previousGDUSincePlantingHistorical = gduHistorical.gdu + previousGDUSincePlantingHistorical;
				}
			}

			previousHistoricalGDU = gduHistorical.gdu + previousHistoricalGDU;
		});

		//Set chart options for gdu data
		const data = [
			{
				name: "30-yr Normal",
				type: "scatter",
				mode: "lines",
				x: histGDUX,
				y: histGDUY,
			},
			{
				name: "Current Year",
				type: "scatter",
				mode: "lines",
				x: currentGDUX,
				y: currentGDUY,
			},
			{
				name: "30-yr Normal Since Planting",
				type: "scatter",
				mode: "lines",
				x: sincePlantingHistX,
				y: sincePlantingHistY,
			},
			{
				name: "Current Year Since Planting",
				type: "scatter",
				mode: "lines",
				x: sincePlantingX,
				y: sincePlantingY,
			},
		];

		setDailyGDUChartOptions({ data, layout: configureChartLayout("", "Cumulative GDUs") });
	}

	function createChartForGDUDaysRainfall() {
		let histRainfallX = [],
			histRainfallY = [];
		let currentRainfallX = [],
			currentRainfallY = [];
		let sincePlantingX = [],
			sincePlantingY = [];
		let sincePlantingHistX = [],
			sincePlantingHistY = [];
		let previousHistoricalRainfall = 0.0;
		let previousRainfall = 0.0;
		let previousRainfallSincePlanting = 0;
		let previousRainfallSincePlantingHistorical = 0.0;
		let lastDaySincePlanting = weatherDataSincePlanting?.find(
			(el, index) => index === weatherDataSincePlanting.length - 1,
		);
		let firstDaySincePlanting = weatherDataSincePlanting?.find((el, index) => index === 0);
		let lastDayReached = false;

		_.map(historicalWeatherData, (rainfallHistorical, index) => {
			//Set historical gdu data for rainfall
			histRainfallX.push(rainfallHistorical.day);
			histRainfallY.push(rainfallHistorical.precipitation + previousHistoricalRainfall);

			//Set current year daily gdu data
			let dailyRainfall = trialDailyWeather?.find((el) => el.day === rainfallHistorical.day);
			if (dailyRainfall) {
				//If daily gdu data exists for the same day as the historical data
				currentRainfallX.push(dailyRainfall.day);
				currentRainfallY.push(dailyRainfall.precipitation + previousRainfall);
				previousRainfall = dailyRainfall.precipitation + previousRainfall;
			}
			//Otherwise set the daily gdu value to empty
			else {
				currentRainfallX.push(rainfallHistorical.day);
				currentRainfallY.push(null);
			}

			//Set current year gdu data for rainfall since planting
			let rainfallSincePlanting =
				rainfallHistorical.day === firstDaySincePlanting?.day
					? null
					: weatherDataSincePlanting?.find((el) => el.day === rainfallHistorical.day);

			let dailyWeatherRainfall = trialDailyWeather?.find((el) => el.day === rainfallSincePlanting?.day);

			//This is used to handle current year situations where we don't have since planting data for the entire year
			if (!lastDayReached) {
				lastDayReached = rainfallHistorical.day === lastDaySincePlanting?.day ? true : false;
			}

			if (dailyWeatherRainfall) {
				//Use previous day's since planting gdus to account of planting date (should be null on plant date)
				let previousGDUFromIndex = historicalWeatherData?.find(
					(el, indexHist) => indexHist === (index === 0 ? 0 : index - 1),
				);
				let previousGDUSincePlantingFromIndex = weatherDataSincePlanting?.find(
					(el) => el.day == previousGDUFromIndex.day,
				);
				let previousGDUSincePlantingHistoricalFromIndex = historicalWeatherData?.find(
					(el) => el.day == previousGDUFromIndex.day,
				);

				//-- Check if all data is found
				if (previousGDUFromIndex && previousGDUSincePlantingFromIndex && previousGDUSincePlantingHistoricalFromIndex) {
					sincePlantingX.push(dailyWeatherRainfall.day);
					sincePlantingY.push(previousGDUSincePlantingFromIndex.precipitation + previousRainfallSincePlanting);
					sincePlantingHistX.push(rainfallHistorical.day);
					sincePlantingHistY.push(
						previousGDUSincePlantingHistoricalFromIndex.precipitation + previousRainfallSincePlantingHistorical,
					);
					previousRainfallSincePlanting =
						previousGDUSincePlantingFromIndex.precipitation + previousRainfallSincePlanting;
					previousRainfallSincePlantingHistorical =
						previousGDUSincePlantingHistoricalFromIndex.precipitation + previousRainfallSincePlantingHistorical;
				}
			} else {
				sincePlantingX.push(rainfallHistorical.day);
				sincePlantingY.push(null);
				//Normal operation if we don't have since planting data
				if (!lastDayReached) {
					sincePlantingHistX.push(rainfallHistorical.day);
					sincePlantingHistY.push(null);
				}
				//Set since planting GDU data from historical weather data
				else {
					sincePlantingHistX.push(rainfallHistorical.day);
					sincePlantingHistY.push(rainfallHistorical.precipitation + previousRainfallSincePlantingHistorical);
					previousRainfallSincePlantingHistorical =
						rainfallHistorical.precipitation + previousRainfallSincePlantingHistorical;
				}
			}

			previousHistoricalRainfall = rainfallHistorical.precipitation + previousHistoricalRainfall;
		});

		const data = [
			{
				name: "30-yr Normal",
				type: "scatter",
				mode: "lines",
				x: histRainfallX,
				y: histRainfallY,
			},
			{
				name: "Current Year",
				type: "scatter",
				mode: "lines",
				x: currentRainfallX,
				y: currentRainfallY,
			},
			{
				name: "30-yr Normal Since Planting",
				type: "scatter",
				mode: "lines",
				x: sincePlantingHistX,
				y: sincePlantingHistY,
			},
			{
				name: "Current Year Since Planting",
				type: "scatter",
				mode: "lines",
				x: sincePlantingX,
				y: sincePlantingY,
			},
		];

		setCumulativeRainfallChartOptions({ data, layout: configureChartLayout("", "Cumulative Rainfall (inches)") });
	}

	return loading || (dailyChartLoading && gduChartLoading) ? (
		<Loader active className={styles.workaround} inline="centered" />
	) : (
		<Grid columns={2} divided>
			<Grid.Row>
				<Grid.Column floated="right">
					<Button
						disabled={
							loading ||
							dailyChartLoading ||
							!userAuth.hasApplicationArea(ApplicationArea.TrialWeatherCRUD, userAuth.currentClientId)
						}
						primary={!loading && !dailyChartLoading}
						loading={excelDownloadLoading}
						content="Weather Data Download"
						icon="download"
						labelPosition="left"
						floated="right"
						onClick={() => {
							generateWeatherDataExcel();
						}}
					/>
				</Grid.Column>
			</Grid.Row>

			{!isCropValid ? (
				<Message
					warning
					content={"The crop for this trial is not set up to calculate GDUs"}
					style={{ marginTop: "unset", width: "100%", textAlign: "center" }}
				/>
			) : null}
			<Grid.Row
				stretched
				style={{ border: "1px solid var(--border)", borderRadius: "8px", background: "#ffffff", margin: "0 15px 15px" }}
			>
				<Grid.Column>
					<AFPlot
						data={dailyTempChartOptions.data}
						layout={dailyTempChartOptions.layout}
						config={{ toImageButtonOptions: { filename: `${trialName}_DailyTemperature` } }}
						useResizeHandler
						style={{ width: "100%" }}
					/>
				</Grid.Column>
				<Grid.Column>
					<AFPlot
						data={dailyRainfallChartOptions.data}
						layout={dailyRainfallChartOptions.layout}
						config={{ toImageButtonOptions: { filename: `${trialName}_DailyRainfall` } }}
						useResizeHandler
						style={{ width: "100%" }}
					/>
				</Grid.Column>
				<Grid.Column>
					<AFPlot
						data={dailyGDUChartOptions.data}
						layout={dailyGDUChartOptions.layout}
						config={{ toImageButtonOptions: { filename: `${trialName}_CumulativeGDUs` } }}
						useResizeHandler
						style={{ width: "100%" }}
					/>
				</Grid.Column>
				<Grid.Column>
					<AFPlot
						data={cumulativeRainfallChartOptions.data}
						layout={cumulativeRainfallChartOptions.layout}
						config={{ toImageButtonOptions: { filename: `${trialName}_CumulativeRainfall` } }}
						useResizeHandler
						style={{ width: "100%" }}
					/>
				</Grid.Column>
			</Grid.Row>
			<Message style={{ margin: "0 15px", width: "100%", textAlign: "center", borderRadius: "8px" }}>
				<p>
					These data were obtained from the NASA Langley Research Center (LaRC) POWER Project funded through the NASA
					Earth Science/Applied Science Program.
					<span>
						<Popup
							content="NASA POWER processes weather data from multiple sources over varying lengths of time. aerialFARMS strives to maintain up-to-date parity with NASA POWER's current data outputs, but users may notice some discrepancies between NASA POWER and aerialFARMS. Please visit NASA POWER's site to learn more: https://power.larc.nasa.gov/docs/methodology/."
							trigger={<Icon name="info circle" link />}
						/>
					</span>
				</p>
			</Message>
		</Grid>
	);
};
TrialWeatherGraphs.propTypes = {
	trialName: PropTypes.string,
	setGrowingSeason: PropTypes.func,
};
export default TrialWeatherGraphs;
